/**
 * Mise en forme pour l'affichage.
 *
 * Hors du noyau : ce module a le droit d'utiliser `Math` et des nombres nus,
 * puisqu'il est précisément la frontière où une grandeur cesse d'être une
 * grandeur pour devenir du texte.
 */

import type { Quantity, Unit } from "../core/units.js";
import { valueIn } from "../core/units.js";

/** Virgule décimale, comme il se doit en français. */
export function formatNumber(value: number, decimals: number): string {
  return value.toFixed(decimals).replace(".", ",");
}

/**
 * Le nom d'une unité tel qu'on l'écrit, par opposition à son jeton.
 *
 * Le vocabulaire d'unités est fermé et fait pour être tapé dans du code :
 * `m/s2` s'y saisit sans caractère exotique. Ce n'est pas ce qu'un lecteur
 * attend, et lui montrer le jeton reviendrait à lui montrer un détail
 * d'implémentation. La table ne contient que les unités dont l'écriture
 * diffère ; toutes les autres se donnent telles quelles.
 */
const UNIT_LABELS: Partial<Record<Unit, string>> = {
  "m/s2": "m/s²",
};

export function unitLabel(unit: Unit): string {
  return UNIT_LABELS[unit] ?? unit;
}

/** Écart en sigmas, signe compris — le signe porte du sens. */
export function formatSigma(q: Quantity<"dimensionless">): string {
  const value = valueIn(q, "1");
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatNumber(value, 1)} σ`;
}

/** Jour julien → date civile, sans passer par `Date` (Fliegel–Van Flandern). */
function calendarOf(julianDay: Quantity<"time">): {
  day: number;
  month: number;
  year: number;
} {
  const jd = valueIn(julianDay, "d") + 0.5;
  const z = Math.floor(jd);
  const alpha = Math.floor((z - 1867216.25) / 36524.25);
  const a = z + 1 + alpha - Math.floor(alpha / 4);
  const b = a + 1524;
  const c = Math.floor((b - 122.1) / 365.25);
  const d = Math.floor(365.25 * c);
  const e = Math.floor((b - d) / 30.6001);

  const day = b - d - Math.floor(30.6001 * e);
  const month = e < 14 ? e - 1 : e - 13;
  return { day, month, year: month > 2 ? c - 4716 : c - 4715 };
}

const pad = (n: number): string => String(n).padStart(2, "0");

/** Date lisible à partir d'un jour julien, sans passer par `Date`. */
export function formatInstant(julianDay: Quantity<"time">): string {
  const { day, month, year } = calendarOf(julianDay);
  return `${pad(day)}/${pad(month)}/${year}`;
}

/**
 * Jour et mois seulement, pour une graduation d'axe.
 *
 * Une date complète fait environ 55 unités de large ; un axe en compte 632.
 * Répéter l'année sur chaque graduation la rendait illisible — l'année se dit
 * une fois, dans la description du graphe.
 */
export function formatDayMonth(julianDay: Quantity<"time">): string {
  const { day, month } = calendarOf(julianDay);
  return `${pad(day)}/${pad(month)}`;
}

/** L'année seule : elle quitte les graduations, elle doit se dire ailleurs. */
export function formatYear(julianDay: Quantity<"time">): string {
  return String(calendarOf(julianDay).year);
}

const TEXT_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
};

const ATTRIBUTE_ESCAPES: Readonly<Record<string, string>> = {
  ...TEXT_ESCAPES,
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Neutralise le balisage venu des données, pour un **contenu textuel**.
 *
 * Un dataset est un fichier fourni de l'extérieur : ses chaînes ne sont jamais
 * du balisage de confiance. En contenu textuel, seuls `&`, `<` et `>` ont
 * besoin d'être échappés — échapper aussi les apostrophes rendrait le français
 * illisible pour rien.
 */
export function escape(text: string): string {
  return text.replace(/[&<>]/g, (char) => TEXT_ESCAPES[char] ?? char);
}

/** Même chose pour une **valeur d'attribut**, où les guillemets comptent. */
export function escapeAttribute(text: string): string {
  return text.replace(/[&<>"']/g, (char) => ATTRIBUTE_ESCAPES[char] ?? char);
}
